const paramMatcher = /:[a-zA-Z0-9]+/g;

function createRouteEntry([path, callback]) {
  let pattern = "^",
    lastIndex = 0,
    match,
    paramNames = [];
  const unbound = path.slice(-1) != "*";

  while ((match = paramMatcher.exec(path))) {
    const [paramName] = match;
    paramNames.push(paramName.slice(1));

    pattern += path.slice(lastIndex, match.index) + "([^/]*)";
    lastIndex = match.index + paramName.length;
  }

  pattern += path.slice(lastIndex, unbound ? undefined : -1);

  if (unbound) {
    pattern += "$";
  }

  const regex = new RegExp(pattern);

  const matcher = (path) => {
    const match = regex.exec(path);
    if (!match) return [undefined, {}];

    const [string, ...values] = match;
    const params = paramNames.reduce(
      (obj, name, i) => ({
        ...obj,
        [name]: values[i],
      }),
      {}
    );

    return [string, params];
  };

  return {
    matcher,
    callback,
  };
}

/**
 * Internal representation of the router. The router is represented through a list of
 * objects containing both a matcher function to match against a path, and a callback to
 * execute when the route is matched.
 *
 * @typedef {Array<{matcher: (path: String) => [String, Object], callback: VoidFunction}>} Routes
 */

/**
 * Creates a router from a list of routes.
 * The routes are represented though a map of paths to callbacks.
 * The path can contain wildcards `*` at the end to match any path.
 *
 * The path may contain parameters in the form of `:paramName`. The parameter name can
 * only contain lowercase letters, uppercase letters and numbers.
 *
 * The callback will be called with an object containing the parameters.
 *
 * ## Example
 *
 * ```js
 * const routes = createRoutes({
 *   "/": () => console.log("Home"),
 *   "/users/:userId": ({ userId }) => console.log("User", userId),
 *   "/users/*": () => console.log("Catch all route"),
 * });
 * ```
 *
 * @param {{[path: String]: VoidFunction}} routes
 * @returns {Routes}
 */
export function createRoutes(routes) {
  return Object.entries(routes).map(createRouteEntry);
}

/**
 * Match a path against a list of routes.
 *
 * ## Example
 *
 * ```js
 * const routes = createRoutes({
 *   "/users/:userId": ({ userId }) => console.log("User", userId),
 * });
 * match(routes, "/users/123");
 * // log: "User 123"
 * ```
 * @param {Routes} routes
 * @param {String} pathname
 * @returns {Object | undefined} - A object containing the return value of the callback and
 *   the parameters parsed in the URL. `undefined` is returned if no route matched.
 */
export function match(routes, pathname) {
  let match, params;
  for (const { matcher, callback } of routes) {
    [match, params] = matcher(pathname);
    if (match === undefined) continue;
    const returnValue = callback(params);
    return { returnValue, params };
  }
}

/**
 * Tagged templates to generate and validate Digiforma routes.
 *
 * The equivalent of Phoenix's VerifiedRoutes but for JavaScript.
 * Whether the route exists is validated by a "verified-routes"
 * ESLint rule.
 *
 * ## Differences with Phoenix's VerifiedRoutes
 *
 * - There is no `Phoenix.Param` protocol, you cannot pass an object
 *   and expect the `id` field to be used as the parameter,
 *   `/training_sessions/${trainingSession}` will not work.
 *
 * ## Usage
 *
 *     p`/training_sessions/${123}`
 *     "/training_sessions/123"
 *
 *     p`/users/log_in?email=${"john@example.com"}`
 *     "/users/log_in?email=john%40example.com"
 *
 *     p`/not_found`
 *     Error: Path /not_found does not exist
 *
 * @returns {String}
 */
window.p = function sigilP(parts, ...params) {
  let pathParts = parts.flatMap((part, idx) => {
    let param = params[idx];

    if (typeof param === "object" && param !== null) {
      // `?${params}` => `?param1=value1&param2=value2`
      const queryParams = Object.keys(param)
        .map((key) => `${key}=${encodeURIComponent(param[key])}`)
        .join("&");

      return [part, queryParams];
    }

    return [part, encodeURIComponent(param)];
  });

  // the last element is always "undefined", there is one less params than parts
  pathParts.pop();

  return pathParts.join("");
};
